import { useEffect, type RefObject } from 'react';

type ScrollAxis = 'x' | 'y';

const AXIS_RESET_MS = 140;
const TOUCH_LOCK_PX = 6;

/**
 * On a 2D scrollport, keep a gesture on one axis so vertical pans do not drift
 * horizontally (and the reverse). Applies to wheel and touch.
 *
 * Touch path sets scrollTop/scrollLeft from the finger delta, which intentionally
 * trades away iOS inertial/momentum fling inside the scrollport — needed so a
 * dense matrix (sticky headers + many columns) does not diagonal-drift. Wheel
 * only rewrites deltas when the locked axis would otherwise pick up cross-axis
 * movement.
 *
 * `remountKey` must change when the scroll node is recreated (e.g. row/column
 * count or canManage), so the effect re-queries `.data-table-wrapper` / the
 * custom selector after React replaces that element.
 */
export function useLockScrollAxis(
    containerRef: RefObject<HTMLElement | null>,
    scrollSelector = '.data-table-wrapper',
    remountKey?: string | number,
) {
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;

        const el =
            (scrollSelector ? root.querySelector<HTMLElement>(scrollSelector) : null) ??
            (root instanceof HTMLElement ? root : null);
        if (!el) return;

        let wheelAxis: ScrollAxis | null = null;
        let wheelResetTimer: number | undefined;

        const scheduleWheelUnlock = () => {
            window.clearTimeout(wheelResetTimer);
            wheelResetTimer = window.setTimeout(() => {
                wheelAxis = null;
            }, AXIS_RESET_MS);
        };

        const onWheel = (event: WheelEvent) => {
            const absX = Math.abs(event.deltaX);
            const absY = Math.abs(event.deltaY);
            if (absX < 1 && absY < 1) return;

            if (!wheelAxis) {
                wheelAxis = absX > absY ? 'x' : 'y';
            }
            scheduleWheelUnlock();

            if (wheelAxis === 'y' && absX > 0) {
                event.preventDefault();
                el.scrollTop += event.deltaY;
                return;
            }

            if (wheelAxis === 'x' && absY > 0) {
                event.preventDefault();
                el.scrollLeft += event.deltaX;
            }
        };

        let touchAxis: ScrollAxis | null = null;
        let touchStartX = 0;
        let touchStartY = 0;
        let startScrollLeft = 0;
        let startScrollTop = 0;

        const onTouchStart = (event: TouchEvent) => {
            const touch = event.touches[0];
            if (!touch) return;
            touchAxis = null;
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            startScrollLeft = el.scrollLeft;
            startScrollTop = el.scrollTop;
        };

        const onTouchMove = (event: TouchEvent) => {
            const touch = event.touches[0];
            if (!touch) return;

            const dx = touch.clientX - touchStartX;
            const dy = touch.clientY - touchStartY;

            if (!touchAxis) {
                if (Math.abs(dx) < TOUCH_LOCK_PX && Math.abs(dy) < TOUCH_LOCK_PX) return;
                touchAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            }

            event.preventDefault();
            if (touchAxis === 'y') {
                el.scrollTop = startScrollTop - dy;
            } else {
                el.scrollLeft = startScrollLeft - dx;
            }
        };

        const onTouchEnd = () => {
            touchAxis = null;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);

        return () => {
            window.clearTimeout(wheelResetTimer);
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [containerRef, remountKey, scrollSelector]);
}
